import {useMemo} from 'react'
import type {LogEntry, SessionState} from '../types/session'
import {isCompletionToolMessage, isToolOutputsMessage, SessionState as SessionStateValues} from '../types/session'
import {isAbortMessage} from '../types/session-events'
import type {PendingPromptResult} from '../components/session-logs/prompt-log-utils'
import {extractPromptFromEntry, getPromptToolCallId} from '../components/session-logs/prompt-log-utils'

const NO_PENDING_PROMPT: PendingPromptResult = {
  pendingPrompt: null,
  pendingToolCallId: null,
  hasPendingPrompt: false,
  isExpired: false,
}

/**
 * Build a set of all tool call IDs that have been resolved (responded to or aborted).
 * Single O(N) pass over logs.
 *
 * When `unresolvedPermissionState` is true (the session is in `waiting_for_user`
 * or `timed_out`), `role: "tool"` messages are NOT counted as resolutions.
 * Mission Control is the source of truth for "user has not yet responded":
 * when the gate times out, the runtime emits a synthetic tool rejection
 * (`"The user rejected this tool call. User feedback: Permission request timed
 * out…"`) so the engine can keep the loop sane. That message lands in the
 * session log and would otherwise mark the permission_request as resolved,
 * hiding the prompt state the UI should surface. Aborts and tool_outputs
 * (explicit user-driven acknowledgements) still resolve, so a real user
 * response or cancel still advances the FIFO.
 */
function buildResolvedToolCallIds(logs: LogEntry[], unresolvedPermissionState: boolean): Set<string> {
  const resolved = new Set<string>()
  const seenPromptIds = new Set<string>()

  for (const entry of logs) {
    if (isCompletionToolMessage(entry)) {
      if (!unresolvedPermissionState) {
        resolved.add(entry.tool_call_id)
      }
      continue
    }
    if (isToolOutputsMessage(entry)) {
      resolved.add(entry.toolCallId)
      continue
    }

    const promptId = getPromptToolCallId(entry)
    if (promptId) {
      seenPromptIds.add(promptId)
      continue
    }

    if (isAbortMessage(entry)) {
      for (const id of seenPromptIds) {
        resolved.add(id)
      }
    }
  }

  return resolved
}

/**
 * Computes the earliest unresolved interactive prompt from session logs.
 * Uses FIFO ordering so that when multiple prompts are pending,
 * the user addresses them in the order they arrived.
 *
 * `sessionState`, when set to `waiting_for_user` or `timed_out`, tells the
 * resolver that Mission Control is authoritatively waiting for or has timed out
 * a user response — so the runtime's synthetic timeout-rejection tool message
 * must NOT be treated as a real resolution. See {@link buildResolvedToolCallIds}
 * for details.
 */
export function computePendingPrompt(
  logs: LogEntry[] | undefined,
  additionalResolvedIds?: ReadonlySet<string>,
  sessionState?: SessionState,
): PendingPromptResult {
  if (!logs?.length) {
    return NO_PENDING_PROMPT
  }

  const unresolvedPermissionState =
    sessionState === SessionStateValues.WaitingForUser || sessionState === SessionStateValues.TimedOut
  const resolvedIds = buildResolvedToolCallIds(logs, unresolvedPermissionState)
  if (additionalResolvedIds) {
    for (const id of additionalResolvedIds) {
      resolvedIds.add(id)
    }
  }

  for (const entry of logs) {
    const prompt = extractPromptFromEntry(entry)
    if (prompt && !resolvedIds.has(prompt.toolCallId)) {
      return {
        pendingPrompt: prompt,
        pendingToolCallId: prompt.toolCallId,
        hasPendingPrompt: true,
        // Expiration is driven by Mission Control's session state. CLI sessions never expire.
        isExpired: sessionState === SessionStateValues.TimedOut,
      }
    }
  }

  return NO_PENDING_PROMPT
}

/**
 * Hook that detects the earliest unresolved interactive prompt
 * (permission_request, ask_user, exit_plan_mode) from session logs.
 *
 * Works for any session that emits the supported prompt log entries —
 * not specific to CLI sessions.
 */
export function usePendingPrompt(
  logs: LogEntry[] | undefined,
  additionalResolvedIds?: ReadonlySet<string>,
  sessionState?: SessionState,
): PendingPromptResult {
  return useMemo(
    () => computePendingPrompt(logs, additionalResolvedIds, sessionState),
    [logs, additionalResolvedIds, sessionState],
  )
}
